'use client';

// useCreatorMessage - Hook for managing creator message flow state
// Story 4.11: Send a Message to My Creator

import { useState, useCallback, useEffect } from 'react';

export type CreatorMessageStep =
  | 'closed'
  | 'message'
  | 'contact'
  | 'name'
  | 'confirm'
  | 'sending'
  | 'done'
  | 'error';

export interface CreatorMessageData {
  message: string;
  contactEmail: string;
  contactPhone: string;
  contactName: string;
}

const initialData: CreatorMessageData = {
  message: '',
  contactEmail: '',
  contactPhone: '',
  contactName: '',
};

function getSentKey(botId: string) {
  return `boty_creator_msg_sent_${botId}`;
}

export function useCreatorMessage(botId: string, sessionId: string) {
  const [step, setStep] = useState<CreatorMessageStep>('closed');
  const [data, setData] = useState<CreatorMessageData>(initialData);
  const [hasSent, setHasSent] = useState(false);

  // Check sessionStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && botId) {
      const sent = sessionStorage.getItem(getSentKey(botId));
      if (sent === 'true') setHasSent(true);
    }
  }, [botId]);

  const open = useCallback(() => {
    if (!hasSent) {
      setStep('message');
      setData(initialData);
    }
  }, [hasSent]);

  const close = useCallback(() => {
    setStep('closed');
  }, []);

  const updateData = useCallback((partial: Partial<CreatorMessageData>) => {
    setData((prev) => ({ ...prev, ...partial }));
  }, []);

  const goToStep = useCallback((nextStep: CreatorMessageStep) => {
    setStep(nextStep);
  }, []);

  const submit = useCallback(async () => {
    setStep('sending');
    try {
      const res = await fetch(`/api/bots/${botId}/creator-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: data.message,
          contactEmail: data.contactEmail || null,
          contactPhone: data.contactPhone || null,
          contactName: data.contactName || null,
        }),
      });

      if (!res.ok) {
        setStep('error');
        return;
      }

      setStep('done');
      setHasSent(true);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(getSentKey(botId), 'true');
      }
    } catch {
      setStep('error');
    }
  }, [botId, sessionId, data]);

  const reset = useCallback(() => {
    setStep('message');
    setData(initialData);
  }, []);

  return {
    step,
    data,
    hasSent,
    open,
    close,
    updateData,
    goToStep,
    submit,
    reset,
  };
}
